<template>
  <div 
    class="mascot-display"
    :class="{ 'mascot-clickable': hasAvailableTutorials }"
    :style="mascotStyles"
    @click="handleClick"
  >
    <!-- Mascot Image -->
    <div class="mascot-image-container">
      <img 
        :src="currentMascotImage"
        :alt="`PecTutor mascot in ${state.current} state`"
        class="mascot-image"
        :class="animationClass"
        @error="handleImageError"
      />
      
      <!-- Pulse indicator when tutorials are available -->
      <div 
        v-if="hasAvailableTutorials && state.current === 'idle'"
        class="tutorial-indicator"
      ></div>
    </div>

    <!-- Speech bubble for quick messages -->
    <div 
      v-if="showBubble"
      class="speech-bubble"
      :class="`bubble-${bubbleType}`"
    >
      {{ bubbleText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import type { MascotState, PecTutorConfig } from '../types';

// Props
interface Props {
  state: MascotState;
  config: PecTutorConfig;
  hasAvailableTutorials?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasAvailableTutorials: false
});

// Emits
const emit = defineEmits<{
  click: [];
}>();

// Reactive state
const showBubble = ref(false);
const bubbleText = ref('');
const bubbleType = ref<'idle' | 'greeting' | 'hint'>('idle');
const imageError = ref(false);

// Computed properties
const currentMascotImage = computed(() => {
  if (imageError.value) {
    // Fallback to a simple, colored SVG circle if images fail
    return 'data:image/svg+xml,' + encodeURIComponent(`
      <svg width="120" height="120" xmlns="http://www.w3.org/2000/svg">
        <rect width="120" height="120" fill="${props.config.theme.primaryColor}" rx="60"/>
        <circle cx="40" cy="40" r="8" fill="white"/>
        <circle cx="80" cy="40" r="8" fill="white"/>
        <path d="M 30 70 Q 60 90 90 70" stroke="white" stroke-width="4" fill="none" stroke-linecap="round"/>
        <text x="60" y="110" text-anchor="middle" fill="white" font-size="12">PecTutor</text>
      </svg>
    `);
  }
  
  // Dynamically import the mascot images
  if (props.state.current === 'tutorial') {
    return new URL('../assets/mascot-tutorial.png', import.meta.url).href;
  }
  // For all other states ('idle', 'speaking', 'listening', etc.)
  return new URL('../assets/mascot-idle.png', import.meta.url).href;
});

const animationClass = computed(() => {
  switch (props.state.current) {
    case 'speaking':
      return 'mascot-speaking';
    case 'listening':
      return 'mascot-listening';
    case 'thinking':
      return 'mascot-thinking';
    case 'tutorial':
      return 'mascot-tutorial';
    default:
      return '';
  }
});

const mascotStyles = computed(() => ({
  width: `${props.config.mascot.size.width}px`,
  height: `${props.config.mascot.size.height}px`,
  pointerEvents: 'auto',
  transform: `translate(${props.state.position.x}px, ${props.state.position.y}px)`,
  transition: `transform ${props.config.animation.mascotTransition}ms ease`,
}));

// Methods
const handleClick = () => {
  if (props.hasAvailableTutorials) {
    emit('click');
  } else {
    showGreeting();
  }
};

const handleImageError = () => {
  console.warn('PecTutor: Mascot image failed to load, using fallback SVG');
  imageError.value = true;
};

const showGreeting = () => {
  bubbleText.value = 'Oi! Eu sou o PecTutor 👋';
  bubbleType.value = 'greeting';
  showBubble.value = true;
  
  setTimeout(() => {
    showBubble.value = false;
  }, 2000);
};

const showHint = () => {
  if (props.hasAvailableTutorials) {
    bubbleText.value = 'Clique em mim para tutoriais!';
    bubbleType.value = 'hint';
    showBubble.value = true;
    
    setTimeout(() => {
      showBubble.value = false;
    }, 3000);
  }
};

// Watch for state changes
watch(() => props.state.current, (newState, oldState) => {
  if (newState === 'idle' && oldState !== 'idle' && props.hasAvailableTutorials) {
    // Show hint after returning to idle state
    setTimeout(() => {
      showHint();
    }, 1000);
  }
});

// Lifecycle
onMounted(() => {
  // Show initial hint if tutorials are available
  if (props.hasAvailableTutorials) {
    setTimeout(() => {
      showHint();
    }, 3000);
  }
});
</script>

<style scoped>
.mascot-display {
  position: relative;
  cursor: default;
  user-select: none;
}

.mascot-clickable {
  cursor: pointer;
}

.mascot-image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.mascot-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
  transition: all 0.3s ease;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.mascot-clickable .mascot-image:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3));
}

/* Animation states */
.mascot-speaking {
  animation: speaking 0.5s ease-in-out infinite alternate;
}

.mascot-listening {
  animation: listening 2s ease-in-out infinite;
}

.mascot-thinking {
  animation: thinking 1s ease-in-out infinite;
}

.mascot-tutorial {
  animation: tutorial-ready 0.3s ease;
}

/* Tutorial indicator */
.tutorial-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  background: #ff4757;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.tutorial-indicator::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

/* Speech bubble */
.speech-bubble {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;
  padding: 8px 12px;
  background: var(--pec-tutor-background, white);
  border: 2px solid var(--pec-tutor-primary, #007bff);
  border-radius: var(--pec-tutor-border-radius, 8px);
  color: var(--pec-tutor-text, #333);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: bubbleAppear 0.3s ease;
  z-index: 1;
}

.speech-bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-top-color: var(--pec-tutor-primary, #007bff);
}

.speech-bubble.bubble-greeting {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.speech-bubble.bubble-greeting::after {
  border-top-color: #4CAF50;
}

.speech-bubble.bubble-hint {
  background: #FF9800;
  border-color: #FF9800;
  color: white;
  animation: bubbleAppear 0.3s ease, bounce 0.5s ease 0.3s;
}

.speech-bubble.bubble-hint::after {
  border-top-color: #FF9800;
}

/* Animations */
@keyframes speaking {
  0% { transform: scale(1) rotate(-1deg); }
  100% { transform: scale(1.05) rotate(1deg); }
}

@keyframes listening {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

@keyframes thinking {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes tutorial-ready {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes bubbleAppear {
  0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
  40% { transform: translateX(-50%) translateY(-5px); }
  60% { transform: translateX(-50%) translateY(-3px); }
}
</style>
